<template>
  <div class="inflation-calculator-page">
    <header class="page-header">
      <h1 class="typo-headline-4 page-title">Calculadoras de Inflación</h1>
      <p class="typo-body-1 page-subtitle">
        Calcula la variación de precios provocada por la inflación en diferentes escenarios.
      </p>
    </header>

    <!-- Tabs Section - Vanilla Implementation -->
    <section class="tabs-section">
      <div class="vanilla-tabs-container">
        <button
          v-for="tab in tabOptions"
          :key="tab.tab_id"
          @click="handleTabClick(tab.tab_id)"
          :class="['vanilla-tab-button', { 'active': activeTabId === tab.tab_id }]"
        >
          {{ tab.tab_name }}
        </button>
      </div>
    </section>

    <section v-if="activeTabId === 'annual_cpi'" class="chart-display-section card">
      <h2 class="typo-headline-6 section-title">Visualización de Inflación Anual (España)</h2>
      <div ref="annualCpiChartContainer" class="chart-container">
        <p v-if="isLoadingAnnualCpi" class="loading-text">Cargando datos del gráfico...</p>
      </div>
    </section>

    <section v-if="activeTabId === 'monthly_cpi'" class="chart-display-section card">
      <h2 class="typo-headline-6 section-title">Visualización de Inflación Mensual (España)</h2>
      <div ref="monthlyCpiChartContainer" class="chart-container">
        <p v-if="isLoadingMonthlyCpi" class="loading-text">Cargando datos del gráfico...</p>
      </div>
    </section>

    <!-- Spain Historical Inflation Calculator -->
    <section class="calculator-section card">
      <h2 class="typo-headline-5 section-title">
        Calculadora de Inflación Histórica en España
        <span class="typo-caption">(Datos 1999-{{ currentYear }})</span>
      </h2>
      <p class="typo-body-2 section-description">
        Calcula cómo el valor del dinero ha cambiado entre dos años específicos en España.
      </p>
      <form @submit.prevent="calculateSpainInflation" class="calculator-form">
        <div class="form-row">
          <!-- Vanilla Input for Initial Amount -->
          <div class="vanilla-input-group form-input">
            <label :for="inputConfig.spainAmount.input_key_to_save_user_values" class="vanilla-input-label">{{ inputConfig.spainAmount.input_label }}</label>
            <input
              type="text"
              inputmode="decimal"
              :id="inputConfig.spainAmount.input_key_to_save_user_values"
              v-model="spainHistoricalForm.initialAmount"
              @input="onDecimalInput('spainHistoricalForm.initialAmount', $event.target.value)"
              :placeholder="inputConfig.spainAmount.input_label"
              class="vanilla-input-field"
            />
            <small v-if="inputConfig.spainAmount.input_helper" class="vanilla-input-helper">{{ inputConfig.spainAmount.input_helper }}</small>
          </div>
          <!-- Vanilla Input for Start Year -->
          <div class="vanilla-input-group form-input">
            <label :for="inputConfig.spainStartYear.input_key_to_save_user_values" class="vanilla-input-label">{{ inputConfig.spainStartYear.input_label }}</label>
            <input
              type="text"
              inputmode="numeric"
              :id="inputConfig.spainStartYear.input_key_to_save_user_values"
              v-model="spainHistoricalForm.startYear"
              @input="onIntegerInput('spainHistoricalForm.startYear', $event.target.value)"
              :placeholder="inputConfig.spainStartYear.input_label"
              class="vanilla-input-field"
            />
            <small v-if="inputConfig.spainStartYear.input_helper" class="vanilla-input-helper">{{ inputConfig.spainStartYear.input_helper }}</small>
          </div>
        </div>
        <div class="form-row">
          <!-- Vanilla Input for End Year -->
          <div class="vanilla-input-group form-input">
            <label :for="inputConfig.spainEndYear.input_key_to_save_user_values" class="vanilla-input-label">{{ inputConfig.spainEndYear.input_label }}</label>
            <input
              type="text"
              inputmode="numeric"
              :id="inputConfig.spainEndYear.input_key_to_save_user_values"
              v-model="spainHistoricalForm.endYear"
              @input="onIntegerInput('spainHistoricalForm.endYear', $event.target.value)"
              :placeholder="inputConfig.spainEndYear.input_label"
              class="vanilla-input-field"
            />
            <small v-if="inputConfig.spainEndYear.input_helper" class="vanilla-input-helper">{{ inputConfig.spainEndYear.input_helper }}</small>
          </div>
          <div class="form-input form-input--placeholder"></div>
        </div>
        <button type="submit" class="btn btn-primary btn-calculate">
          Calcular Equivalencia
        </button>
      </form>
      <div v-if="spainHistoricalForm.showResult" class="result-display" ref="spainCalcResultDisplay">
        <p class="typo-body-1">
          <strong class="result-value">{{ spainHistoricalForm.resultAmount }}€</strong>
          <br />
          El poder adquisitivo de
          <strong>{{ spainHistoricalForm.calculated.initialAmount }}€</strong> en
          <strong>{{ spainHistoricalForm.calculated.startYear }}</strong> equivale a
          <strong>{{ spainHistoricalForm.resultAmount }}€</strong> en
          <strong>{{ spainHistoricalForm.calculated.endYear }}</strong>.
        </p>
        <div ref="spainCalcChartContainer" class="chart-container--small"></div>
      </div>
    </section>

    <!-- Future Value Calculator -->
    <section class="calculator-section card">
      <h2 class="typo-headline-5 section-title">Cálculo de Valor Futuro Estimado</h2>
      <p class="typo-body-2 section-description">
        Estima cómo cambiará el valor de una cantidad de dinero en el futuro, dada una tasa de inflación promedio.
      </p>
      <form @submit.prevent="calculateFutureValue" class="calculator-form">
        <div class="form-row">
          <div class="vanilla-input-group form-input">
            <label :for="inputConfig.futureAmount.input_key_to_save_user_values" class="vanilla-input-label">{{ inputConfig.futureAmount.input_label }}</label>
            <input
              type="text"
              inputmode="decimal"
              :id="inputConfig.futureAmount.input_key_to_save_user_values"
              v-model="futureProjectionForm.currentAmount"
              @input="onDecimalInput('futureProjectionForm.currentAmount', $event.target.value)"
              :placeholder="inputConfig.futureAmount.input_label"
              class="vanilla-input-field"
            />
            <small v-if="inputConfig.futureAmount.input_helper" class="vanilla-input-helper">{{ inputConfig.futureAmount.input_helper }}</small>
          </div>
          <div class="vanilla-input-group form-input">
            <label :for="inputConfig.futureAvgInflation.input_key_to_save_user_values" class="vanilla-input-label">{{ inputConfig.futureAvgInflation.input_label }}</label>
            <input
              type="text"
              inputmode="decimal"
              :id="inputConfig.futureAvgInflation.input_key_to_save_user_values"
              v-model="futureProjectionForm.avgInflationRate"
              @input="onDecimalInput('futureProjectionForm.avgInflationRate', $event.target.value)"
              :placeholder="inputConfig.futureAvgInflation.input_label"
              class="vanilla-input-field"
            />
             <small v-if="inputConfig.futureAvgInflation.input_helper" class="vanilla-input-helper">{{ inputConfig.futureAvgInflation.input_helper }}</small>
          </div>
          <div class="vanilla-input-group form-input">
            <label :for="inputConfig.futurePeriod.input_key_to_save_user_values" class="vanilla-input-label">{{ inputConfig.futurePeriod.input_label }}</label>
            <input
              type="text"
              inputmode="numeric"
              :id="inputConfig.futurePeriod.input_key_to_save_user_values"
              v-model="futureProjectionForm.years"
              @input="onIntegerInput('futureProjectionForm.years', $event.target.value)"
              :placeholder="inputConfig.futurePeriod.input_label"
              class="vanilla-input-field"
            />
            <small v-if="inputConfig.futurePeriod.input_helper" class="vanilla-input-helper">{{ inputConfig.futurePeriod.input_helper }}</small>
          </div>
        </div>
        <button type="submit" class="btn btn-primary btn-calculate">
          Calcular Valor Futuro
        </button>
      </form>
      <div v-if="futureProjectionForm.showResult" class="result-display" ref="futureCalcResultDisplay">
        <p class="typo-body-1">
          <strong class="result-value">{{ futureProjectionForm.resultAmount }}</strong>
          <br />
          Con una inflación promedio del
          <strong>{{ futureProjectionForm.calculated.avgInflationRate }}%</strong>, el poder adquisitivo de
          <strong>{{ futureProjectionForm.calculated.currentAmount }}€</strong> hoy, equivaldrá a
          <strong>{{ futureProjectionForm.resultAmount }}</strong> en
          <strong>{{ futureProjectionForm.calculated.years }}</strong> años.
        </p>
      </div>
    </section>

    <!-- Past Value Calculator -->
    <section class="calculator-section card">
      <h2 class="typo-headline-5 section-title">Cálculo de Valor Pasado Estimado</h2>
      <p class="typo-body-2 section-description">
        Estima cuál habría sido el valor equivalente de una cantidad de dinero en el pasado, dada una tasa de inflación promedio.
      </p>
      <form @submit.prevent="calculatePastValue" class="calculator-form">
        <div class="form-row">
          <div class="vanilla-input-group form-input">
            <label :for="inputConfig.pastAmount.input_key_to_save_user_values" class="vanilla-input-label">{{ inputConfig.pastAmount.input_label }}</label>
            <input
              type="text"
              inputmode="decimal"
              :id="inputConfig.pastAmount.input_key_to_save_user_values"
              v-model="pastProjectionForm.currentAmount"
              @input="onDecimalInput('pastProjectionForm.currentAmount', $event.target.value)"
              :placeholder="inputConfig.pastAmount.input_label"
              class="vanilla-input-field"
            />
            <small v-if="inputConfig.pastAmount.input_helper" class="vanilla-input-helper">{{ inputConfig.pastAmount.input_helper }}</small>
          </div>
          <div class="vanilla-input-group form-input">
            <label :for="inputConfig.pastAvgInflation.input_key_to_save_user_values" class="vanilla-input-label">{{ inputConfig.pastAvgInflation.input_label }}</label>
            <input
              type="text"
              inputmode="decimal"
              :id="inputConfig.pastAvgInflation.input_key_to_save_user_values"
              v-model="pastProjectionForm.avgInflationRate"
              @input="onDecimalInput('pastProjectionForm.avgInflationRate', $event.target.value)"
              :placeholder="inputConfig.pastAvgInflation.input_label"
              class="vanilla-input-field"
            />
            <small v-if="inputConfig.pastAvgInflation.input_helper" class="vanilla-input-helper">{{ inputConfig.pastAvgInflation.input_helper }}</small>
          </div>
          <div class="vanilla-input-group form-input">
            <label :for="inputConfig.pastPeriod.input_key_to_save_user_values" class="vanilla-input-label">{{ inputConfig.pastPeriod.input_label }}</label>
            <input
              type="text"
              inputmode="numeric"
              :id="inputConfig.pastPeriod.input_key_to_save_user_values"
              v-model="pastProjectionForm.years"
              @input="onIntegerInput('pastProjectionForm.years', $event.target.value)"
              :placeholder="inputConfig.pastPeriod.input_label"
              class="vanilla-input-field"
            />
            <small v-if="inputConfig.pastPeriod.input_helper" class="vanilla-input-helper">{{ inputConfig.pastPeriod.input_helper }}</small>
          </div>
        </div>
        <button type="submit" class="btn btn-primary btn-calculate">
          Calcular Valor Pasado
        </button>
      </form>
      <div v-if="pastProjectionForm.showResult" class="result-display" ref="pastCalcResultDisplay">
        <p class="typo-body-1">
          <strong class="result-value">{{ pastProjectionForm.resultAmount }}</strong>
          <br />
          Con una inflación promedio del
          <strong>{{ pastProjectionForm.calculated.avgInflationRate }}%</strong>, una cantidad que hoy vale
          <strong>{{ pastProjectionForm.calculated.currentAmount }}€</strong>, habría equivalido a
          <strong>{{ pastProjectionForm.resultAmount }}</strong> hace
          <strong>{{ pastProjectionForm.calculated.years }}</strong> años.
        </p>
      </div>
    </section>

    <!-- Contact Popup Modal -->
    <!--<transition name="fade">
      <div v-if="contactModal.isVisible" class="modal-overlay" @click.self="closeContactModal">
        <div class="modal-content card">
          <button @click="closeContactModal" class="modal-close-button" aria-label="Cerrar modal">
            <span class="icon-close">×</span>
          </button>
          <div class="modal-header">
            <img src="../assets/img/eurekers.jpg" alt="Eurekers Logo" class="modal-logo" />
            <h3 class="typo-headline-6 modal-title">Conoce más Herramientas</h3>
          </div>
          <p class="typo-body-2 modal-description">
            En Eurekers tenemos herramientas como esta para ayudarte a gestionar tus inversiones. Déjanos tus datos si deseas más información.
          </p>
          <form @submit.prevent="submitContactForm" class="modal-form">
            <div class="vanilla-input-group form-input form-input--modal">
              <label :for="inputConfig.popupPhone.input_key_to_save_user_values" class="vanilla-input-label">{{ inputConfig.popupPhone.input_label }}</label>
              <input
                type="tel" 
                inputmode="tel"
                :id="inputConfig.popupPhone.input_key_to_save_user_values"
                v-model="contactModal.phone"
                :placeholder="inputConfig.popupPhone.input_label"
                class="vanilla-input-field"
              />
              <small v-if="inputConfig.popupPhone.input_helper" class="vanilla-input-helper">{{ inputConfig.popupPhone.input_helper }}</small>
            </div>
            <div class="vanilla-input-group form-input form-input--modal">
              <label :for="inputConfig.popupEmail.input_key_to_save_user_values" class="vanilla-input-label">{{ inputConfig.popupEmail.input_label }}</label>
              <input
                type="email"
                inputmode="email"
                :id="inputConfig.popupEmail.input_key_to_save_user_values"
                v-model="contactModal.email"
                :placeholder="inputConfig.popupEmail.input_label"
                class="vanilla-input-field"
              />
              <small v-if="inputConfig.popupEmail.input_helper" class="vanilla-input-helper">{{ inputConfig.popupEmail.input_helper }}</small>
            </div>
            <button type="submit" class="btn btn-primary btn-send">
              Enviar Información
            </button>
          </form>
        </div>
      </div>
    </transition>-->
  </div>
</template>

<script>
import Highcharts from "highcharts";
import { mapGetters } from "vuex";
import Vue from 'vue';
import APICaller from "@/components/codeComponents/APICaller.js";

import inputInflationConfig from './configs/inflation-calculator/input-inflation-calculator.json';
import inputPopupConfig from './configs/popup/input-popup.json';

const commonChartOptions = {
  credits: { enabled: false },
  chart: {
    backgroundColor: 'var(--color-surface)',
    style: { fontFamily: 'inherit' },
  },
  title: { style: { color: 'var(--color-text-primary)', fontSize: '1.25rem' } },
  subtitle: { style: { color: 'var(--color-text-secondary)' } },
  xAxis: {
    labels: { style: { color: 'var(--color-text-secondary)' } },
    title: { style: { color: 'var(--color-text-primary)' } },
    lineColor: 'var(--color-outline)',
    tickColor: 'var(--color-outline)',
  },
  yAxis: {
    labels: { style: { color: 'var(--color-text-secondary)' } },
    title: { style: { color: 'var(--color-text-primary)' } },
    gridLineColor: 'var(--color-outline)',
  },
  legend: {
    itemStyle: { color: 'var(--color-text-primary)' },
    itemHoverStyle: { color: 'var(--color-highlight)' },
  },
  tooltip: {
    backgroundColor: 'var(--color-active-surface)',
    borderColor: 'var(--color-outline)',
    style: { color: 'var(--color-text-primary)' },
  },
   plotOptions: {
        series: { color: 'var(--color-primary)' } // Default series color
    }
};

export default {
  name: 'InflationCalculatorVanilla',
  mixins: [APICaller],
  metaInfo() {
    return {
      title: 'Calculadoras de Inflación Optimizadas | Eurekers',
      meta: [
        {
          name: 'description',
          content: "Calculadoras de inflación interactivas para España y proyecciones. Herramientas financieras Eurekers."
        }
      ]
    };
  },
  data() {
    const now = new Date();
    return {
      activeTabId: "annual_cpi",
      tabOptions: [
        { tab_name: "Inflación Anual (Gráfico)", tab_id: "annual_cpi" },
        { tab_name: "Inflación Mensual (Gráfico)", tab_id: "monthly_cpi" }
      ],
      currentYear: now.getFullYear(),
      currentMonthPadded: String(now.getMonth() + 1).padStart(2, '0'),
      inputConfig: {
        spainAmount: inputInflationConfig[8],
        spainStartYear: inputInflationConfig[9],
        spainEndYear: inputInflationConfig[11],
        futureAmount: inputInflationConfig[1],
        futureAvgInflation: inputInflationConfig[0],
        futurePeriod: inputInflationConfig[2],
        pastAmount: inputInflationConfig[5],
        pastAvgInflation: inputInflationConfig[4],
        pastPeriod: inputInflationConfig[7],
        popupPhone: inputPopupConfig[0],
        popupEmail: inputPopupConfig[1],
      },
      spainHistoricalForm: {
        initialAmount: '', // Changed from null to empty string for text inputs
        startYear: '',     // Changed from null to empty string
        endYear: '',       // Changed from null to empty string
        resultAmount: '0.00',
        showResult: false,
        calculated: { initialAmount: '', startYear: '', endYear: '' },
        chartData: [],
      },
      spainCpiAnnualData: {},
      spainCpiMonthlyData: {},
      isLoadingAnnualCpi: false,
      isLoadingMonthlyCpi: false,
      futureProjectionForm: {
        currentAmount: '', // Changed
        avgInflationRate: '', // Changed
        years: '', // Changed
        resultAmount: 'RESULTADO (€)',
        showResult: false,
        calculated: { currentAmount: '', avgInflationRate: '', years: '' },
      },
      pastProjectionForm: {
        currentAmount: '', // Changed
        avgInflationRate: '', // Changed
        years: '', // Changed
        resultAmount: 'RESULTADO (€)',
        showResult: false,
        calculated: { currentAmount: '', avgInflationRate: '', years: '' },
      },
      contactModal: {
        isVisible: false,
        phone: '',
        email: '',
        interactionCounter: 0,
      },
      fetchedAnnualCPI: null,
      fetchedMonthlyCPI: null,
      fetchedInflationYears: [],
      fetchedMonthlyCategories: [],
    };
  },
  computed: {
    ...mapGetters(['is_mobile_device']),
  },
  watch: {
    activeTabId(newTabId) {
      this.$nextTick(() => {
        if (newTabId === 'annual_cpi') {
          if (this.fetchedAnnualCPI) this.renderAnnualCpiChart();
          else this.fetchAnnualCpiData();
        } else if (newTabId === 'monthly_cpi') {
          if (this.fetchedMonthlyCPI) this.renderMonthlyCpiChart();
          else this.fetchMonthlyCpiData();
        }
      });
    },
    'spainHistoricalForm.showResult'(newVal) {
        if (newVal) {
            this.$nextTick(() => {
                this.renderSpainCalculationChart();
                if (this.is_mobile_device && this.$refs.spainCalcResultDisplay) {
                  this.scrollToElement(this.$refs.spainCalcResultDisplay);
                }
            });
        }
    },
    'futureProjectionForm.showResult'(newVal) {
        if (newVal && this.is_mobile_device && this.$refs.futureCalcResultDisplay) {
           this.scrollToElement(this.$refs.futureCalcResultDisplay);
        }
    },
    'pastProjectionForm.showResult'(newVal) {
        if (newVal && this.is_mobile_device && this.$refs.pastCalcResultDisplay) {
            this.scrollToElement(this.$refs.pastCalcResultDisplay);
        }
    }
  },
  created() {
    document.getElementsByTagName("html")[0].classList.remove("noscroll");
    if (this.activeTabId === 'annual_cpi') {
      this.fetchAnnualCpiData();
    } else if (this.activeTabId === 'monthly_cpi') {
      this.fetchMonthlyCpiData();
    }
    this.checkIfIframe();
  },
  methods: {
    _sanitizeDecimal(value) {
      let strVal = String(value || '');
      strVal = strVal.replace(/[^0-9.]/g, '');
      const parts = strVal.split('.');
      if (parts.length > 2) {
        strVal = parts[0] + '.' + parts.slice(1).join('');
      }
      return strVal;
    },
    _sanitizeInteger(value) {
      let strVal = String(value || '');
      return strVal.replace(/[^0-9]/g, '');
    },
    onDecimalInput(modelPath, value) {
      const sanitized = this._sanitizeDecimal(value);
      const keys = modelPath.split('.');
      let obj = this;
      for (let i = 0; i < keys.length - 1; i++) {
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = sanitized;
    },
    onIntegerInput(modelPath, value) {
      const sanitized = this._sanitizeInteger(value);
      const keys = modelPath.split('.');
      let obj = this;
      for (let i = 0; i < keys.length - 1; i++) {
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = sanitized;
    },
    handleTabClick(tabId) {
      this.activeTabId = tabId;
    },
    scrollToElement(element) {
        if (element && typeof element.getBoundingClientRect === 'function') {
            this.$nextTick(() => {
                const yOffset = -80; 
                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: 'smooth' });
            });
        }
    },
    async fetchAnnualCpiData() {
      if (this.fetchedAnnualCPI && this.activeTabId === 'annual_cpi') {
        this.$nextTick(this.renderAnnualCpiChart);
        return;
      }
      this.isLoadingAnnualCpi = true;
      const url = `https://sdmx.oecd.org/public/rest/data/OECD.SDD.TPS,DSD_PRICES@DF_PRICES_ALL,1.0/ESP.A.N.CPI.PA._T.N.GY?startPeriod=1999&endPeriod=${this.currentYear}&dimensionAtObservation=AllDimensions`;
      try {
        const response = await this._getAPICallPromise(url);
        const observations = Object.values(response.data.dataSets[0].observations);
        
        this.fetchedAnnualCPI = observations.map(obs => obs[0]);
        
        // Generate fetchedInflationYears based on the number of observations
        const numObservations = this.fetchedAnnualCPI.length;
        const generatedYears = [];
        if (numObservations > 0) {
          for (let i = 0; i < numObservations; i++) {
            generatedYears.push((1999 + i).toString());
          }
        }
        this.fetchedInflationYears = generatedYears;
        
        // Populate spainCpiAnnualData using the generated years and fetched CPI data
        this.spainCpiAnnualData = {};
        this.fetchedInflationYears.forEach((year, index) => {
          if (this.fetchedAnnualCPI[index] !== undefined) {
            this.spainCpiAnnualData[year] = this.fetchedAnnualCPI[index];
          }
        });

        if (this.activeTabId === 'annual_cpi') {
            this.$nextTick(this.renderAnnualCpiChart);
        }
      } catch (error) {
        console.error("Error fetching annual CPI data:", error);
        this.$vueOnToast.pop('error', 'Error al cargar datos de inflación anual.');
        // Clear potentially stale or incomplete data on error
        this.fetchedAnnualCPI = [];
        this.fetchedInflationYears = [];
        this.spainCpiAnnualData = {};
      } finally {
        this.isLoadingAnnualCpi = false;
      }
    },
    async fetchMonthlyCpiData() {
      if (this.fetchedMonthlyCPI && this.fetchedMonthlyCPI.length > 0 && this.activeTabId === 'monthly_cpi') {
         this.$nextTick(this.renderMonthlyCpiChart);
        return;
      }
      this.isLoadingMonthlyCpi = true;
      const url = `https://sdmx.oecd.org/public/rest/data/OECD.SDD.TPS,DSD_PRICES@DF_PRICES_ALL,1.0/ESP.M.N.CPI.PA._T.N.GY?startPeriod=1999-01&endPeriod=${this.currentYear}-${this.currentMonthPadded}&dimensionAtObservation=AllDimensions`;
      try {
        const response = await this._getAPICallPromise(url);
        const rawObservations = response.data.dataSets[0].observations; // This is an object e.g., {"0:0:...:idx": [value]}

        let timeDimension = null;
        if (response.data && response.data.structure && response.data.structure.dimensions && response.data.structure.dimensions.observation) {
            timeDimension = response.data.structure.dimensions.observation.find(dim => dim.id === 'TIME_PERIOD');
        }

        if (!timeDimension || !timeDimension.values) {
            console.error('TIME_PERIOD dimension not found or has no values in API response.');
            this.$vueOnToast.pop('error', 'Error al procesar datos de fecha de la API (dimensión no encontrada).');
            this.isLoadingMonthlyCpi = false;
            return;
        }

        let combinedData = [];
        timeDimension.values.forEach((timeValue, index) => {
            const dateStr = timeValue.name; // Should be "YYYY-MM"
            // Assuming TIME_PERIOD is the 9th dimension and all others are fixed at index 0
            const observationKey = "0:0:0:0:0:0:0:0:" + index; 
            if (rawObservations[observationKey]) {
                const cpiValue = rawObservations[observationKey][0];
                combinedData.push({ date: dateStr, value: cpiValue });
            } else {
                // This case might occur if data is sparse or API structure changes
                console.warn(`No observation data for key ${observationKey} (date: ${dateStr})`);
            }
        });

        // Sort combinedData by date chronologically ("YYYY-MM" strings sort naturally)
        combinedData.sort((a, b) => {
            if (a.date < b.date) return -1;
            if (a.date > b.date) return 1;
            return 0;
        });

        this.fetchedMonthlyCategories = combinedData.map(item => item.date);
        this.fetchedMonthlyCPI = combinedData.map(item => item.value);

        this.spainCpiMonthlyData = {};
        this.fetchedMonthlyCategories.forEach((monthYear, idx) => {
            this.spainCpiMonthlyData[monthYear] = this.fetchedMonthlyCPI[idx];
        });

        if (this.activeTabId === 'monthly_cpi') {
            this.$nextTick(this.renderMonthlyCpiChart);
        }
      } catch (error) {
        console.error("Error fetching or processing monthly CPI data:", error);
        this.$vueOnToast.pop('error', 'Error al cargar o procesar datos de inflación mensual.');
      } finally {
        this.isLoadingMonthlyCpi = false;
      }
    },
    calculateSpainInflation() {
      const { initialAmount, startYear, endYear } = this.spainHistoricalForm;
      if (initialAmount === '' || startYear === '' || endYear === '') {
        this.$vueOnToast.pop('warning', 'Por favor, completa todos los campos.');
        return;
      }
      const numInitialAmount = parseFloat(initialAmount);
      const numStartYear = parseInt(startYear, 10);
      const numEndYear = parseInt(endYear, 10);

      if (isNaN(numInitialAmount) || numInitialAmount < 0 || isNaN(numStartYear) || isNaN(numEndYear)) {
        this.$vueOnToast.pop('error', 'Valores numéricos inválidos.');
        return;
      }

      // Determine the latest year for which data is available
      let latestDataYear = 0;
      if (this.fetchedInflationYears && this.fetchedInflationYears.length > 0) {
        const numericYears = this.fetchedInflationYears.map(y => parseInt(y, 10)).filter(y => !isNaN(y));
        if (numericYears.length > 0) {
            latestDataYear = Math.max(...numericYears);
        }
      }
      
      if (latestDataYear === 0 && this.spainCpiAnnualData && Object.keys(this.spainCpiAnnualData).length > 0) {
        const numericKeys = Object.keys(this.spainCpiAnnualData).map(y => parseInt(y, 10)).filter(y => !isNaN(y));
        if (numericKeys.length > 0) {
            latestDataYear = Math.max(...numericKeys);
        }
      }

      const validationMaxYear = latestDataYear > 0 ? latestDataYear : this.currentYear;

      if (numStartYear < 1999 || numStartYear > validationMaxYear || numEndYear < 1999 || numEndYear > validationMaxYear) {
        this.$vueOnToast.pop('error', `Los años deben estar entre 1999 y ${validationMaxYear}.`);
        return;
      }

      if (numEndYear < numStartYear) {
        this.$vueOnToast.pop('error', 'El año final no puede ser anterior al año inicial.');
        return;
      }
      
      if (!this.fetchedAnnualCPI || Object.keys(this.spainCpiAnnualData).length === 0) {
          this.$vueOnToast.pop('error', 'Datos de inflación no disponibles. Intente de nuevo.');
          this.fetchAnnualCpiData(); // Attempt to refetch
          return;
      }
      
      let compoundedValue = numInitialAmount;
      let chartSeriesData = [{ year: numStartYear, value: numInitialAmount }];

      for (let year = numStartYear + 1; year <= numEndYear; year++) {
          const yearStr = String(year);
          const yearlyRateValue = this.spainCpiAnnualData[yearStr];
          if (yearlyRateValue === undefined) { 
              this.$vueOnToast.pop('error', `Faltan datos de inflación para el año ${year}. Datos disponibles hasta ${validationMaxYear}.`);
              this.spainHistoricalForm.showResult = false;
              return;
          }
          const yearlyRate = yearlyRateValue / 100;
          compoundedValue *= (1 + yearlyRate);
          chartSeriesData.push({ year: year, value: parseFloat(compoundedValue.toFixed(2))});
      }
      
      this.spainHistoricalForm.resultAmount = compoundedValue.toFixed(2);
      this.spainHistoricalForm.calculated = { initialAmount: numInitialAmount, startYear: numStartYear, endYear: numEndYear };
      this.spainHistoricalForm.chartData = chartSeriesData;

      const wasShowingResult = this.spainHistoricalForm.showResult;
      this.spainHistoricalForm.showResult = true;

      if (wasShowingResult) {
        this.$nextTick(() => {
            this.renderSpainCalculationChart();
            if (this.is_mobile_device && this.$refs.spainCalcResultDisplay) {
                this.scrollToElement(this.$refs.spainCalcResultDisplay);
            }
        });
      } 

      this.incrementInteractionCounterAndShowPopup();
    },
    calculateFutureValue() {
      const { currentAmount, avgInflationRate, years } = this.futureProjectionForm;
      if (currentAmount === '' || avgInflationRate === '' || years === '') {
        this.$vueOnToast.pop('warning', 'Por favor, completa todos los campos.');
        return;
      }
      const P = parseFloat(currentAmount);
      const r = parseFloat(avgInflationRate) / 100;
      const t = parseInt(years, 10);

      if (isNaN(P) || P < 0 || isNaN(r) || isNaN(t) || t < 0) {
        this.$vueOnToast.pop('error', 'Valores inválidos. Asegúrate de usar números y períodos positivos.');
        return;
      }
      const futureValue = P * Math.pow(1 + r, t);
      this.futureProjectionForm.resultAmount = futureValue.toFixed(2) + '€';
      this.futureProjectionForm.calculated = { currentAmount: P, avgInflationRate: parseFloat(avgInflationRate), years: t };
      this.futureProjectionForm.showResult = true;
      this.incrementInteractionCounterAndShowPopup();
    },
    calculatePastValue() {
      const { currentAmount, avgInflationRate, years } = this.pastProjectionForm;
      if (currentAmount === '' || avgInflationRate === '' || years === '') {
        this.$vueOnToast.pop('warning', 'Por favor, completa todos los campos.');
        return;
      }
      const F = parseFloat(currentAmount);
      const r = parseFloat(avgInflationRate) / 100;
      const t = parseInt(years, 10);

      if (isNaN(F) || F < 0 || isNaN(r) || isNaN(t) || t < 0) {
        this.$vueOnToast.pop('error', 'Valores inválidos. Asegúrate de usar números y períodos positivos.');
        return;
      }
      const pastValue = F / Math.pow(1 + r, t);
      this.pastProjectionForm.resultAmount = pastValue.toFixed(2) + '€';
      this.pastProjectionForm.calculated = { currentAmount: F, avgInflationRate: parseFloat(avgInflationRate), years: t };
      this.pastProjectionForm.showResult = true;
      this.incrementInteractionCounterAndShowPopup();
    },
    incrementInteractionCounterAndShowPopup() {
      this.contactModal.interactionCounter++;
      const emailStored = window.localStorage.getItem("email");
      const phoneStored = window.localStorage.getItem("phone_number");
      if ((this.contactModal.interactionCounter === 3 || this.contactModal.interactionCounter === 10) && (!emailStored && !phoneStored)) {
        this.contactModal.isVisible = true;
        document.getElementsByTagName("html")[0].classList.add("noscroll");
      }
    },
    closeContactModal() {
      this.contactModal.isVisible = false;
      document.getElementsByTagName("html")[0].classList.remove("noscroll");
    },
    validatePhoneNumber(phone) { return /^\d{9}$/.test(phone); },
    validateEmail(email) { return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email); },
    async submitContactForm() {
      const { phone, email } = this.contactModal;
      if (!this.validatePhoneNumber(phone)) {
        this.$vueOnToast.pop('error', 'Número de teléfono incorrecto (debe tener 9 dígitos).');
        return;
      }
      if (!this.validateEmail(email)) {
        this.$vueOnToast.pop('error', 'Formato de email incorrecto.');
        return;
      }
      window.localStorage.setItem("email", email);
      window.localStorage.setItem("phone_number", phone);
      let data = new URLSearchParams();
      data.append('email', email);
      data.append('tlf', phone);
      const url = "/calculadora/datos";
      try {
        const response = await this._postAPICallPromise(url, data);
        if (response.status === 200 || (response.data && response.data.status === 200) ) {
          this.$vueOnToast.pop('success', 'Información enviada con éxito.');
          this.closeContactModal();
        } else {
          this.$vueOnToast.pop('error', 'Error al enviar la información. Inténtelo de nuevo.');
        }
      } catch (error) {
        console.error("Error submitting contact form:", error);
        this.$vueOnToast.pop('error', 'Error de conexión al enviar. Inténtelo más tarde.');
      }
    },
    renderAnnualCpiChart() {
        if (!this.$refs.annualCpiChartContainer || !this.fetchedAnnualCPI || !this.fetchedAnnualCPI.length) {
            if (this.activeTabId === 'annual_cpi' && !this.isLoadingAnnualCpi) this.isLoadingAnnualCpi = true;
            return;
        }
        this.isLoadingAnnualCpi = false;
        
        // Generate array of years from 1999 to current year or last available year
        const startYear = 1999;
        const endYear = Math.min(this.currentYear, startYear + this.fetchedAnnualCPI.length - 1);
        const years = [];
        
        for (let year = startYear; year <= endYear; year++) {
            years.push(year.toString());
        }
        
        // Create data array with values and colors
        const seriesData = this.fetchedAnnualCPI.map(rate => {
            const value = parseFloat(rate);
            return {
                y: value,
                color: value >= 0 ? 'var(--color-error)' : 'var(--color-success)'
            };
        });
        
        Highcharts.chart(this.$refs.annualCpiChartContainer, Highcharts.merge(commonChartOptions, {
            chart: { type: 'column' },
            title: { text: 'Tasa de Inflación Anual en España (%)' },
            subtitle: { text: `Fuente: OECD (1999-${endYear})` },
            xAxis: { 
                categories: years,
                title: { text: 'Año' }, 
                crosshair: true 
            },
            yAxis: { 
                title: { text: 'Variación Anual (%)' }, 
                min: Math.min(...seriesData.map(item => item.y), 0) - 1 
            },
            tooltip: { 
                headerFormat: '<span style="font-size:10px">{point.key}</span><table>', 
                pointFormat: '<tr><td style="padding:0">Inflación: </td><td style="padding:0"><b>{point.y:.2f} %</b></td></tr>', 
                footerFormat: '</table>', 
                shared: true, 
                useHTML: true 
            },
            plotOptions: { 
                column: { 
                    pointPadding: 0.1, 
                    borderWidth: 0, 
                    groupPadding: 0.1
                } 
            },
            series: [{ 
                name: 'Inflación Anual', 
                data: seriesData, 
                showInLegend: false 
            }]
        }));
    },
    renderMonthlyCpiChart() {
        if (!this.$refs.monthlyCpiChartContainer || !this.fetchedMonthlyCPI || !this.fetchedMonthlyCPI.length || !this.fetchedMonthlyCategories || !this.fetchedMonthlyCategories.length) {
            if (this.activeTabId === 'monthly_cpi' && !this.isLoadingMonthlyCpi) this.isLoadingMonthlyCpi = true;
            return;
        }
        this.isLoadingMonthlyCpi = false;
        const seriesData = this.fetchedMonthlyCPI.map(rate => parseFloat(rate)); 

        let rawLastMonthYearInData = this.fetchedMonthlyCategories.length > 0 
            ? this.fetchedMonthlyCategories[this.fetchedMonthlyCategories.length - 1] 
            : `${this.currentYear}-${this.currentMonthPadded}`;
        // The replace for "Spain" is no longer strictly necessary for lastMonthYearInData if categories are correct, but harmless.
        const lastMonthYearInData = rawLastMonthYearInData.replace(/Spain[,\s]*/i, '').trim(); 
         
        // The replace for "Spain" is no longer strictly necessary for cleanedCategories if categories are correct, but harmless.
        const cleanedCategories = this.fetchedMonthlyCategories.map(cat => {
            return String(cat).replace(/Spain[,\s]*/i, '').trim(); // Ensure cat is a string
        });

        // Removed debug logs

        Highcharts.chart(this.$refs.monthlyCpiChartContainer, Highcharts.merge(commonChartOptions, {
            chart: { type: 'line' },
            title: { text: 'Tasa de Inflación Mensual en España (%)' },
            subtitle: { text: `Fuente: OECD (Variación interanual mensual, hasta ${lastMonthYearInData})` },
            xAxis: { 
              type: 'category',
              categories: cleanedCategories, 
              title: { text: '' },
              labels: { 
                rotation: -45,
                formatter: function() {
                  return String(this.value); // this.value should now be the date string like "YYYY-MM"
                }
              }, 
              tickInterval: Math.max(1, Math.floor(cleanedCategories.length / 10))
            },
            yAxis: { title: { text: 'Variación Mensual (%)' } },
            tooltip: { headerFormat: '<span style="font-size:10px">{point.key}</span><table>', pointFormat: '<tr><td style="color:{series.color};padding:0">Inflación: </td><td style.padding:0"><b>{point.y:.2f} %</b></td></tr>', footerFormat: '</table>', shared: true, useHTML: true },
            series: [{ 
                name: 'Inflación Mensual', 
                data: seriesData, 
                lineWidth: 2, 
                marker: { enabled: false },
                color: 'var(--color-highlight)' 
              }]
        }));
    },
    renderSpainCalculationChart() {
        if (!this.$refs.spainCalcChartContainer || !this.spainHistoricalForm.chartData || !this.spainHistoricalForm.chartData.length) return;
        const categories = this.spainHistoricalForm.chartData.map(d => String(d.year));
        const data = this.spainHistoricalForm.chartData.map(d => d.value);
        Highcharts.chart(this.$refs.spainCalcChartContainer, Highcharts.merge(commonChartOptions, {
            chart: { type: 'line' },
            title: { text: 'Evolución del Valor Adquisitivo' },
            subtitle: { text: `De ${this.spainHistoricalForm.calculated.startYear} a ${this.spainHistoricalForm.calculated.endYear}` },
            xAxis: { categories: categories, title: { text: 'Año' } },
            yAxis: { title: { text: 'Valor Equivalente (€)' } },
            series: [{ name: 'Valor (€)', data: data, color: 'var(--color-highlight)', marker: { enabled: true, symbol: 'circle' } }],
            legend: { enabled: false }
        }));
    },
    checkIfIframe() {
      if (window.location.search.includes("?=iframe")) {
        const sidebar = document.getElementById("sidebar-nav-container");
        if (sidebar) sidebar.style.display = "none";
      }
    },
    _getAPICallPromise(url) {
      return new Promise((resolve, reject) => {
        const successHandler = new APICaller.SuccessHandler(response => resolve(response));
        const failureHandler = new APICaller.FailureHandler('API_GET_ERR', 'API GET Error', error => reject(error));
        this._getAPICall(url, successHandler, failureHandler);
      });
    },
    _postAPICallPromise(url, data) {
      return new Promise((resolve, reject) => {
        const successHandler = new APICaller.SuccessHandler(response => resolve(response));
        const failureHandler = new APICaller.FailureHandler('API_POST_ERR', 'API POST Error', error => reject(error));
        this._postAPICall(url, data, successHandler, failureHandler);
      });
    }
  }
}
</script>

<style scoped>
/* Global Page Styles */
.inflation-calculator-page {
  background-color: var(--color-background);
  color: var(--color-text-primary);
  padding: 20px 5%; /* Added more horizontal padding for breathing room */
  min-height: 100vh;
  max-width: 1200px; /* Max width for very large screens */
  margin: 0 auto; /* Center page content */
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}
.page-title {
  color: var(--color-primary);
  margin-bottom: 8px;
}
.page-subtitle {
  color: var(--color-text-secondary);
  max-width: 600px;
  margin: 0 auto;
}

/* Card base style for sections */
.card {
  background-color: var(--color-surface);
  border-radius: 12px; /* Slightly more rounded */
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07); /* Softer shadow */
  padding: 30px; /* Increased padding within cards */
  margin-bottom: 40px; /* Increased margin between cards */
  border: 1px solid var(--color-outline);
}

/* Vanilla Tabs Implementation */
.tabs-section {
  margin-bottom: 40px;
  display: flex;
  justify-content: center;
}
.vanilla-tabs-container {
  display: flex;
  gap: 10px;
  border-bottom: 2px solid var(--color-outline);
  padding-bottom: 0;
}
.vanilla-tab-button {
  background-color: transparent;
  border: none;
  padding: 12px 20px;
  cursor: pointer;
  color: var(--color-text-secondary);
  font-size: 1rem;
  font-weight: 500;
  transition: color 0.2s ease, border-bottom-color 0.2s ease;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px; /* Align with container border */
  border-radius: 6px 6px 0 0; /* Slight rounding on top */
}
.vanilla-tab-button:hover {
  color: var(--color-primary);
}
.vanilla-tab-button.active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
}

.section-title {
  color: var(--color-text-primary);
  margin-top: 0;
  margin-bottom: 12px;
  border-bottom: 2px solid var(--color-primary);
  padding-bottom: 10px;
  display: inline-block;
}
.section-title .typo-caption {
  color: var(--color-text-secondary);
  font-weight: normal;
  margin-left: 8px;
}
.section-description {
  color: var(--color-text-secondary);
  margin-bottom: 24px;
}

/* Vanilla Input Group Styles */
.vanilla-input-group {
  position: relative;
  margin-bottom: 10px; /* Spacing below each input group */
}
.vanilla-input-label {
  display: block;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  margin-bottom: 6px;
  font-weight: 500;
}
.vanilla-input-field {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid var(--color-outline);
  border-radius: 6px;
  background-color: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box; /* Ensure padding doesn't add to width */
}
/* Hide arrows for text inputs (though type=text doesn't have them by default, some browsers might add for numeric/tel) */
.vanilla-input-field::-webkit-outer-spin-button,
.vanilla-input-field::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.vanilla-input-field[type=number] { /* Just in case any type=number remains or is added later */
  -moz-appearance: textfield;
}

.vanilla-input-field:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-alpha, rgba(15, 174, 215, 0.25)); 
}
.vanilla-input-field::placeholder {
  color: var(--color-text-inactive);
  opacity: 0.7;
}
.vanilla-input-helper {
  display: block;
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  margin-top: 4px;
}

/* Form Styles */
.calculator-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.form-row {
  display: flex;
  gap: 25px; /* Increased gap */
  flex-wrap: wrap;
}
.form-input {
  flex: 1;
  min-width: 220px; /* Increased min-width */
}
.form-input--placeholder { visibility: hidden; }

/* Button Styles */
.btn {
  padding: 12px 28px; /* Slightly more padding */
  border-radius: 25px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.1s ease, box-shadow 0.2s ease;
  border: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 180px;
  align-self: flex-start;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
.btn-primary {
  background-color: var(--color-highlight);
  color: var(--color-text-on-highlight); 
}
.btn-primary:hover {
  background-color: var(--color-secondary);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}
.btn-primary:active {
  transform: scale(0.98);
  background-color: var(--color-highlight);
}
.btn-calculate {
  margin-top: 10px;
}

/* Result Display */
.result-display {
  margin-top: 30px; /* Increased margin */
  padding: 25px;
  background-color: var(--color-active-surface);
  border-left: 5px solid var(--color-primary);
  border-radius: 8px; /* More rounding */
  color: var(--color-text-primary);
}
.result-display p { margin: 0; line-height: 1.6; }
.result-value {
  font-size: 2em; /* Larger result value */
  color: var(--color-highlight);
  display: block;
  margin-bottom: 10px;
}

/* Chart Containers */
.chart-display-section .chart-container { height: 400px; margin-top: 20px; }
.chart-container--small { height: 300px; margin-top: 20px; }
.loading-text { text-align: center; color: var(--color-text-secondary); padding: 20px; }

/* Modal Styles */
.modal-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.65); /* Darker overlay */
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
  padding: 20px; /* Ensure modal is not touching edges on small screens */
}
.modal-content {
  position: relative; padding: 35px; width: 100%; max-width: 550px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15); /* More prominent shadow */
}
.modal-close-button {
  position: absolute; top: 15px; right: 15px;
  background: transparent; border: none;
  color: var(--color-text-secondary); cursor: pointer;
  padding: 8px; line-height: 1;
  border-radius: 50%;
  transition: background-color 0.2s ease, color 0.2s ease;
}
.modal-close-button:hover {
  background-color: var(--color-active-surface);
  color: var(--color-text-primary);
}
.icon-close { font-size: 24px; font-weight: bold; } 

.modal-logo {
  width: 60px; height: 60px; border-radius: 50%; margin-bottom: 15px;
  border: 2px solid var(--color-outline);
}
.modal-title { color: var(--color-primary); margin-bottom: 10px; }
.modal-description { color: var(--color-text-secondary); margin-bottom: 25px; }
.modal-form { display: flex; flex-direction: column; gap: 20px; }
.form-input--modal { min-width: auto; }
.btn-send { align-self: center; margin-top: 10px; }

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* Responsive Adjustments */
@media (max-width: 768px) {
  .inflation-calculator-page { padding: 20px; } /* Adjust padding for mobile */
  .card { padding: 20px; margin-bottom: 30px; }
  .form-row { flex-direction: column; gap: 20px; }
  .form-input { min-width: 100%; }
  .btn { align-self: stretch; text-align: center; }
  .modal-content { padding: 25px; }
  .vanilla-tabs-container { gap: 5px; overflow-x: auto; /* Allow scrolling for many tabs */ }
  .vanilla-tab-button { padding: 10px 15px; font-size: 0.9rem; }
}

.noscroll { overflow: hidden; }

/* Typography helpers */
.typo-headline-4 { font-size: 2.125rem; font-weight: 500; line-height: 1.2; }
.typo-headline-5 { font-size: 1.5rem; font-weight: 500; line-height: 1.33; }
.typo-headline-6 { font-size: 1.25rem; font-weight: 500; line-height: 1.4; }
.typo-body-1 { font-size: 1rem; font-weight: 400; line-height: 1.5; }
.typo-body-2 { font-size: 0.875rem; font-weight: 400; line-height: 1.5; }
.typo-caption { font-size: 0.75rem; font-weight: 400; line-height: 1.66; }

/* Define primary alpha for box-shadow if not globally available */
:root {
  /* Ensure your color variables are defined here or globally */
  /* --color-primary: #0faed7; */ /* Example */
  /* --color-highlight: #YOUR_HIGHLIGHT_COLOR */ /* Example */
  --color-primary-alpha: rgba(15, 174, 215, 0.25); /* Adjust if your primary is different */
}

</style>